// Serviço para comunicação com API de dados
import { apiService } from './api';
import type { DiceType, DiceRollRequest, DiceRollResult, DiceHistory } from '@/types/dice';

class DiceService {
  
  /**
   * Rola dados via API
   */
  async rollDice(request: DiceRollRequest): Promise<DiceRollResult> {
    try {
      const response = await apiService.rollDice(request);
      return response;
    } catch (error: any) {
      console.error('Erro ao rolar dado:', error);
      
      // Tratar erros específicos
      if (error.status === 429) {
        throw new Error('Muitas rolagens de dados. Tente novamente em alguns segundos.');
      } else if (error.status === 403) {
        throw new Error('Você não tem permissão para rolar dados nesta mesa.');
      } else {
        throw new Error('Erro ao rolar dado. Tente novamente.');
      }
    }
  }

  /**
   * Busca histórico de rolagens da mesa
   */
  async getDiceHistory(tableId: string, limit: number = 50): Promise<DiceHistory> {
    try {
      const response = await apiService.getDiceHistory(tableId, limit);
      return response;
    } catch (error: any) {
      console.error('Erro ao buscar histórico de dados:', error);
      throw new Error('Erro ao carregar histórico de dados.');
    }
  }

  /**
   * Gera número aleatório local para preview (não confiável para jogo)
   * Usado apenas para feedback visual imediato
   */
  generateLocalPreview(diceType: DiceType): number {
    const diceMap: Record<DiceType, number> = {
      D4: 4,
      D6: 6,
      D8: 8,
      D10: 10,
      D12: 12,
      D20: 20,
      D100: 100
    };

    const max = diceMap[diceType];
    return Math.floor(Math.random() * max) + 1;
  }

  /**
   * Valida tipo de dado
   */
  isValidDiceType(diceType: string): diceType is DiceType {
    const validTypes: DiceType[] = ['D4', 'D6', 'D8', 'D10', 'D12', 'D20', 'D100'];
    return validTypes.includes(diceType as DiceType);
  }

  /**
   * Formatação de resultado para exibição
   */
  formatResult(result: DiceRollResult): string {
    const characterInfo = result.character_name 
      ? ` (${result.character_name})` 
      : '';

    // Teste de atributo: a conta inteira, no mesmo formato que o servidor
    // escreve no chat (docs/PLANO_TESTE_ATRIBUTO.md).
    if (result.attribute_name) {
      const quem = result.character_name || result.user_name;
      const bonus = result.bonus
        ? ` ${result.bonus > 0 ? '+' : '−'} ${Math.abs(result.bonus)}`
        : '';
      const conta = `${result.dice_type} ${result.result} + ${result.attribute_value} (${result.attribute_name})${bonus} = ${result.total}`;
      if (result.difficulty === undefined || result.difficulty === null) {
        return `🎲 ${quem} testou ${result.attribute_name}: ${conta}`;
      }
      const veredito = result.outcome === 'success' ? 'sucesso' : 'falha';
      return `🎲 ${quem} testou ${result.attribute_name}: ${conta} vs ${result.difficulty} — ${veredito}`;
    }

    return `🎲 ${result.user_name}${characterInfo} rolou ${result.dice_type}: **${result.result}**`;
  }

  /**
   * Análise de estatísticas simples
   */
  analyzeRolls(history: DiceRollResult[]): {
    averageRoll: Record<DiceType, number>;
    totalRolls: Record<DiceType, number>;
    criticalHits: number;
    criticalFails: number;
  } {
    const stats = {
      averageRoll: {} as Record<DiceType, number>,
      totalRolls: {} as Record<DiceType, number>,
      criticalHits: 0,
      criticalFails: 0
    };

    const rollSums: Record<DiceType, number> = {} as Record<DiceType, number>;

    for (const roll of history) {
      const diceType = roll.dice_type;
      
      if (!stats.totalRolls[diceType]) {
        stats.totalRolls[diceType] = 0;
        rollSums[diceType] = 0;
      }

      stats.totalRolls[diceType]++;
      rollSums[diceType] += roll.result;

      // Critical hits (máximo) e fails (1)
      if (diceType === 'D20') {
        if (roll.result === 20) stats.criticalHits++;
        if (roll.result === 1) stats.criticalFails++;
      }
    }

    // Calcular médias
    for (const diceType of Object.keys(rollSums) as DiceType[]) {
      stats.averageRoll[diceType] = rollSums[diceType] / stats.totalRolls[diceType];
    }

    return stats;
  }
}

export const diceService = new DiceService();